import React, { useState } from "react";
import { Box, Typography, CircularProgress, Alert, Grid } from "@mui/material";
import SubfolderList from "./SubfolderList";
import FileList from "./FileList";
import AdminReviewControls from "./AdminReviewControls";
import SummaryDrawer from "./SummaryDrawer";
import FilePreview from "./FilePreview";
import BreadcrumbNavigation from "./component/BreadcrumbNavigation";
import AnimatedText from "../../user/file_management/component/AnimatedText";
import {
  usePendingFiles,
  usePendingFolders,
  usePendingSubfolders,
} from "./hooks/usePendingFileHierarchy";
import { useQueryClient } from "@tanstack/react-query";

function PendingSubmission({ onFolderSelect, onSubfolderSelect }) {
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedSubfolder, setSelectedSubfolder] = useState(null);
  const [activeBreadcrumb, setActiveBreadcrumb] = useState(1);

  //For Opening File Preview
  const [previewFile, setPreviewFile] = useState(null);
  const handleViewFile = (file) => {
    setPreviewFile(file);
  };
  const handleClosePreview = () => {
    setPreviewFile(null);
  };

  //Opening the summary in file preview state
  const [summaryOpen, setSummaryOpen] = useState(false);
  const handleToggleSummary = () => setSummaryOpen((open) => !open);

  const queryClient = useQueryClient();

  const {
    data: folders,
    isLoading: foldersLoading,
    error: foldersError,
  } = usePendingFolders();

  const {
    data: subfolders,
    isLoading: subfoldersLoading,
    error: subfoldersError,
  } = usePendingSubfolders(selectedFolder?.userId, selectedFolder?.folderId);

  const {
    data: files,
    isLoading: filesLoading,
    error: filesError,
  } = usePendingFiles(
    selectedFolder?.userId,
    selectedFolder?.folderId,
    selectedSubfolder?.id
  );

  const breadcrumbItems = [
    {
      key: "status",
      label: "Pending Submissions",
      show: true,
    },
    {
      key: "folder",
      label: selectedFolder
        ? selectedFolder.folderName ||
          selectedFolder.folderId ||
          selectedFolder.id
        : "",
      show: !!selectedFolder,
    },
    {
      key: "subfolder",
      label: selectedSubfolder ? selectedSubfolder.subfolderName : "",
      show: !!selectedSubfolder,
    },
  ];

  const handleBreadcrumbClick = (idx) => {
    setActiveBreadcrumb(idx);
    if (idx === 0) {
      setSelectedFolder(null);
      setSelectedSubfolder(null);
    } else if (idx === 1) {
      setSelectedSubfolder(null);
    } else if (idx === 2) {
      // No change needed, already at subfolder level
    }
  };

  const handleFolderSelectInternal = (folder) => {
    setSelectedFolder(folder);
    setSelectedSubfolder(null);
    setActiveBreadcrumb(1);
    if (onFolderSelect) {
      onFolderSelect(folder);
    }
  };

  const handleSubfolderSelectInternal = (subfolder) => {
    setSelectedSubfolder(subfolder);
    setActiveBreadcrumb(2);
    if (onSubfolderSelect) {
      onSubfolderSelect(subfolder);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <BreadcrumbNavigation
        breadcrumbItems={breadcrumbItems}
        activeIndex={activeBreadcrumb}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      <AnimatedText text="Pending Submissions" />

      {foldersLoading ? (
        <CircularProgress />
      ) : foldersError ? (
        <Alert severity="error">Failed to load folders.</Alert>
      ) : !folders || folders.length === 0 ? (
        <Typography>No pending submissions found.</Typography>
      ) : selectedFolder ? (
        <>
          <Typography variant="h6" gutterBottom>
            {selectedFolder.folderName || selectedFolder.id}
          </Typography>
          <Grid container spacing={2} sx={{ justifyContent: "center" }}>
            <Grid item xs={4}>
              {subfoldersLoading ? (
                <CircularProgress />
              ) : subfoldersError ? (
                <Alert severity="error">Failed to load subfolders.</Alert>
              ) : (
                <SubfolderList
                  subfolders={subfolders || []}
                  selectedSubfolder={selectedSubfolder}
                  onSubfolderSelect={handleSubfolderSelectInternal}
                />
              )}
            </Grid>
            <Grid item xs={8}>
              {filesLoading ? (
                <CircularProgress />
              ) : filesError ? (
                <Alert severity="error">Failed to load files.</Alert>
              ) : selectedSubfolder ? (
                <FileList files={files || []} onViewFile={handleViewFile} />
              ) : (
                <Typography>Select a subfolder to view files.</Typography>
              )}
            </Grid>
          </Grid>
          <AdminReviewControls
            folder={selectedFolder}
            onStatusChange={() => {
              // Refresh folders or clear selection on status change
              setSelectedFolder(null);
              setSelectedSubfolder(null);
              setActiveBreadcrumb(0);
              // Invalidate queries to refresh data
              queryClient.invalidateQueries({ queryKey: ["pendingFolders"] });
              queryClient.invalidateQueries({
                queryKey: ["pendingSubfolders"],
              });
              queryClient.invalidateQueries({ queryKey: ["pendingFiles"] });
            }}
          />
        </>
      ) : (
        <Grid container spacing={2}>
          {folders.map((folder) => (
            <Grid item key={folder.id} xs={6} sm={4} md={3} lg={2}>
              <Box
                onClick={() => handleFolderSelectInternal(folder)}
                sx={{
                  cursor: "pointer",
                  p: 2,
                  borderRadius: 2,
                  backgroundColor:
                    selectedFolder && selectedFolder.id === folder.id
                      ? "rgba(255, 255, 255, 0.1)"
                      : "#121212",
                  boxShadow:
                    selectedFolder && selectedFolder.id === folder.id
                      ? "0 0 10px 2px rgba(11, 107, 203, 0.7)"
                      : "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  transition: "background-color 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    boxShadow: "0 0 10px 2px rgba(255, 255, 255, 0.09)",
                  },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginBottom: 8 }}
                >
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z" />
                </svg>
                <Typography
                  variant="body1"
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {folder.folderName || folder.id}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    mt: 0.5,
                    textAlign: "center",
                  }}
                >
                  {"Submitted by: " + (folder.fullName || "Unknown User")}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      {previewFile && (
        <FilePreview
          open={Boolean(previewFile)}
          onClose={handleClosePreview}
          file={previewFile}
          onToggleSummary={handleToggleSummary}
          summaryOpen={summaryOpen}
        />
      )}
      <SummaryDrawer
        open={summaryOpen}
        onClose={handleToggleSummary}
        summary={previewFile?.summary}
      />
    </Box>
  );
}

export default PendingSubmission;
